import { useEffect, useRef } from "react"
import type { MockChatController, MockTransportMode } from "src/api/chat/MockChatController"
import type { WebChatMessage } from "src/api/chat/Chat"

const POLL_INTERVAL_MS = 2000

export interface UseMockChatSocketOptions {
  chatSessionId: string | null
  onFrame: (frame: WebChatMessage) => void
  enabled?: boolean
  controller: MockChatController
  transportMode: MockTransportMode
}

export function useMockChatSocket({
  chatSessionId,
  onFrame,
  enabled = true,
  controller,
  transportMode,
}: UseMockChatSocketOptions): void {
  const onFrameRef = useRef(onFrame)
  onFrameRef.current = onFrame

  useEffect(() => {
    if (!enabled || !chatSessionId) return

    if (transportMode === "sse") {
      controller.setFrameCallback((frame) => {
        if (frame.chatSessionId === chatSessionId) {
          onFrameRef.current(frame)
        }
      })
      return () => {
        controller.clearFrameCallback()
      }
    }

    // Polling mode
    const interval = setInterval(() => {
      const frames = controller.pollFrames(chatSessionId)
      for (const frame of frames) {
        onFrameRef.current(frame)
      }
    }, POLL_INTERVAL_MS)

    return () => {
      clearInterval(interval)
    }
  }, [chatSessionId, enabled, controller, transportMode])
}
